export type User = {
  identity: Identity;
  providerInfo: ProviderInfo;
  consentCompleted: ConsentCompleted;
  subscription: Subscription;
  results: Results;
  tests: Tests;
  notifications: Notifications;
  ecomm: Ecomm;
  care: Care;
  coaching: Coaching;
  announcements: Announcement[];
  features: Features;
  experiments: Experiments;
  env: Env;
};

type Identity = {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  emailVerified: boolean;
  dateJoined: string;
  isStaff: boolean;
  birthday: string;
  phoneNumber: string | null;
  isProvider: boolean;
  registrationRoute: string | null;
};

type ProviderInfo = {
  isVerified: boolean;
};

type ConsentCompleted = {
  research: boolean;
};

type Subscription = {
  isMember: boolean;
};

type Results = {
  hasReadyTest: boolean;
};

type Tests = {
  totalTestCount: number;
};

type Notifications = {
  care: boolean;
};

type Ecomm = {
  canPurchaseCare: boolean;
};

type Care = {
  vaginitis: {
    hasCompletedConsult: boolean;
  };
  // undefined until the user config has been fetched and cached.
  userEligibleForAnyVaginitisCare: boolean | undefined;
};

type Coaching = {
  numCallsCompleted: number;
  hasUsedFreeCall: boolean;
};

type Features = {
  plan: boolean;
  carePdfDebug: boolean;
  ungatedRxUpsell: boolean;
  vaginitisUpsell: boolean;
  healthHistoryUpsell: boolean;
  providerPdfIncludePlanItems: boolean;
  fertilityV0Enabled: boolean;
  moduleVisibilityEnabled: boolean;
  fertilityModulePdf: boolean;
  dxAtTestResults: boolean;
  bvSubtypesEnabled: boolean;
  malePartnerEnabled: boolean;
  newPDP: boolean;
  urineTestEnabled: boolean;
  fertilityTestEnabled: boolean;
  fertilityResultsPageEnabled: boolean;
  aiChatEnabled: boolean;
  aiChatConsented: boolean;
};

export type Announcement = {
  id: string;
  title: string;
  message: string;
  publishedAt: string;
  viewed_at: string;
};

export type Experiments = {
  problemsModule: boolean;
  prominentOnramp: boolean;
  symptomReliefABTest: boolean;
  // Not every experiment lives here: the results page loads the retest sticky CTA version from
  // user-tests/retest-eligibility, since reading its flag enrolls the user.
};

type Env = {
  name: string;
  frontendUrl: string;
};

// Utils
export const toUser = (userData: any): User => {
  return {
    identity: {
      id: userData?.identity?.id,
      username: userData?.identity?.username,
      firstName: userData?.identity?.first_name,
      lastName: userData?.identity?.last_name,
      email: userData?.identity?.email,
      emailVerified: userData?.identity?.email_verified,
      dateJoined: userData?.identity?.date_joined,
      isStaff: userData?.identity?.is_staff,
      birthday: userData?.identity?.birthday,
      phoneNumber: userData?.identity?.phone_number,
      isProvider: userData?.identity?.is_provider,
      registrationRoute: userData?.identity?.registration_route,
    },
    providerInfo: {
      isVerified: userData?.provider_info?.is_verified,
    },
    consentCompleted: {
      research: userData?.consent_completed?.research,
    },
    subscription: {
      isMember: userData?.subscription?.is_member,
    },
    results: {
      hasReadyTest: userData?.results?.has_ready_test,
    },
    tests: {
      totalTestCount: userData?.tests?.total_test_count,
    },
    notifications: {
      care: userData?.notifications?.care,
    },
    ecomm: {
      canPurchaseCare: userData?.ecomm?.can_purchase_care,
    },
    care: {
      vaginitis: {
        hasCompletedConsult: userData?.care?.vaginitis?.has_completed_consult,
      },
      userEligibleForAnyVaginitisCare:
        userData?.care?.user_eligible_for_any_vaginitis_care,
    },
    coaching: {
      numCallsCompleted: userData?.coaching?.num_calls_completed || 0,
      hasUsedFreeCall: userData?.coaching?.has_used_free_call || false,
    },
    announcements: userData?.announcements?.map((announcement: any) => {
      return {
        id: announcement?.id,
        title: announcement?.title,
        message: announcement?.message,
        publishedAt: announcement?.published_at,
      };
    }),
    features: {
      plan: userData?.features?.plan,
      carePdfDebug: userData?.features?.care_pdf_debug,
      ungatedRxUpsell: userData?.features?.ungated_rx_upsell,
      vaginitisUpsell: userData?.features?.vaginitis_upsell,
      healthHistoryUpsell: userData?.features?.health_history_upsell,
      fertilityV0Enabled: userData?.features?.fertility_v0_enabled,
      moduleVisibilityEnabled: userData?.features?.module_visibility_enabled,
      providerPdfIncludePlanItems:
        userData?.features?.provider_pdf_include_plan_items,
      fertilityModulePdf: userData?.features?.fertility_module_pdf,
      dxAtTestResults: userData?.features?.dx_at_test_results,
      bvSubtypesEnabled: userData?.features?.bv_subtypes_enabled,
      malePartnerEnabled: userData?.features?.male_partner_enabled,
      newPDP: userData?.features?.new_pdp,
      urineTestEnabled: userData?.features?.urine_test_enabled,
      fertilityTestEnabled: userData?.features?.fertility_test_enabled,
      fertilityResultsPageEnabled:
        userData?.features?.fertility_results_page_enabled,
      aiChatEnabled: userData?.features?.ai_chat_enabled,
      aiChatConsented: userData?.features?.ai_chat_consented,
    },
    experiments: {
      problemsModule: userData?.experiments?.problems_module,
      prominentOnramp: userData?.experiments?.prominent_onramp,
      symptomReliefABTest: userData?.experiments?.symptom_relief_a_b_test,
    },
    env: {
      name: userData?.env?.name,
      frontendUrl: userData?.env?.frontend_url,
    },
  };
};

export type ProviderProfile = {
  registrant_role: string;
  registrant_role_options: [string, string][];
  ordering_clinician_names: string;
  provider_type: string;
  provider_type_options: [string, string][];
  provider_type_other: string;
  specialty: string;
  affiliated_practice: string;
  practice_group_affiliation: string;
  practice_group_affiliation_options: [string, string][];
  practice_group_name: string;
  goals: string[];
  goals_options: [string, string][];
  goals_other: string;
  clinic_state: string;
  clinic_state_options: [string, string][];
  clinic_state_options_with_other: [string, string][];
  clinic_state_other: string;
  clinic_country: string;
  clinic_country_options: [string, string][];
  clinic_country_options_with_other: [string, string][];
  clinic_country_other: string;
  clinic_country_other_inquiry: string;
  clinic_province: string;
  clinic_province_options: [string, string][];
  clinic_province_options_with_other: [string, string][];
  clinic_province_other: string;
  patient_types: string[];
  patient_types_options: [string, string][];
  patient_types_other: string;
  patient_encounter_type: string;
  patient_encounter_type_options: [string, string][];
  patient_volume: string;
  patient_volume_options: [string, string][];
  leverage: string[];
  leverage_options: [string, string][];
  leverage_other: string;
  current_testing_methods: string[];
  current_testing_methods_options: [string, string][];
  current_testing_methods_at_home_other: string;
  current_testing_methods_other: string;
  ordering_workflow: string;
  ordering_workflow_options: [string, string][];
  platform_access: string[];
  platform_access_options: [string, string][];
  platform_access_other: string;
  hdyhau: string;
  hdyhau_options: [string, string][];
  hdyhau_other: string;
  verified: boolean;
  has_completed_intake: boolean;
  is_prescribing_provider: boolean;
  consent_terms_conditions_date?: string | null;
  consent_privacy_policy_date?: string | null;
  consent_provider_policy_date?: string | null;
};
